"use client";

import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePathname, useRouter } from "next/navigation";
import { FC, useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import {
    ClientBFFContext,
    formatSearchParamsToString,
    useSearchParamsQuery,
} from "@finbackoffice/site-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Button from "components/base/button/Button";
import Translate from "components/base/translate/Translate";
import { RouteNames, RouterQuery } from "utils/constants";
import Input from "components/base/input-field/Input";
import { ConfigContextV2 } from "contexts";
import AccountHeader from "../../header/AccountHeader";
import styles from "../account-messages.module.sass";

export type IComposeMessageForm = InferType<typeof composeMessageValidationSchema>;

export interface ISendMessageParamsBody {
    id: number;
}

const composeMessageValidationSchema = object({
    topic: string().required(),
    message: string().required(),
});

type IProps = {
    pageMode?: boolean;
};

const ComposeMessage: FC<IProps> = ({ pageMode = false }) => {
    const router = useRouter();
    const pathname = usePathname();
    const { query } = useSearchParamsQuery();
    const [isLoading, setIsLoading] = useState(false);
    const [showMessageSent, setShowMessageSent] = useState(false);
    const client = useContext(ClientBFFContext);
    const { t } = useTranslation(TranslationScopes.UserAccount);
    const { siteMessagingConfig } = useContext(ConfigContextV2);

    const {
        register,
        control,
        handleSubmit,
        reset,
        getValues,
        formState: { isDirty, isSubmitting, isValid },
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(composeMessageValidationSchema),
        defaultValues: {
            topic: "",
            message: "",
        },
    });

    const createMessage = async (chatId: number): Promise<boolean> => {
        try {
            const response = await client.createMessage(chatId, {
                message: getValues().message,
            });
            return Boolean(response);
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const navigateAfterSend = () => {
        if (pageMode) {
            router.push(`/${RouteNames.ACCOUNT}/${RouteNames.MESSAGES}/${RouteNames.SENT}`);
        } else {
            router.replace(
                `${pathname}?${formatSearchParamsToString({ ...query, msgType: RouterQuery.Sent })}`,
            );
        }
    };

    const onSubmit = async (data: IComposeMessageForm) => {
        if (isDirty && !isSubmitting && isValid) {
            setIsLoading(true);
            try {
                const response = await client.createChat({
                    topic: data.topic,
                });

                if (response) {
                    const success = await createMessage(response.id);
                    if (success) {
                        setShowMessageSent(true);
                        setTimeout(() => {
                            setShowMessageSent(false);
                            navigateAfterSend();
                        }, 2000);
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className={styles.messages}>
            {!pageMode && (
                <AccountHeader
                    className={styles.header}
                    headingIcon={`/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/profile-messages-compose.svg`}
                    tId="account_messages_compose_heading"
                />
            )}

            <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.composeMsgForm}
                data-testid="account-compose-message-form">
                <Controller
                    render={({ field: { onChange, value, name } }) => (
                        <Input
                            onChange={onChange}
                            value={value}
                            name={name}
                            label={t("account_messages_subject")}
                            placeholder={t("account_messages_subject")}
                        />
                    )}
                    control={control}
                    name="topic"
                />

                <span>
                    <Translate
                        tid="account_messages_textareaLabel"
                        namespace={TranslationScopes.UserAccount}
                    />
                    :
                </span>
                <textarea
                    placeholder={t("account_messages_messagePlaceholder")}
                    disabled={false}
                    {...register("message")}
                    id="message"
                />
                <div className={styles.btnContainer}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => reset()}
                        disabled={!isDirty}>
                        <Translate tid="base_reset" />
                    </Button>

                    <Button
                        type="submit"
                        disabled={
                            !isDirty ||
                            isSubmitting ||
                            isLoading ||
                            !isValid ||
                            !siteMessagingConfig?.enabled
                        }
                        variant="primary">
                        <Translate
                            tid="account_messages_send"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </Button>
                </div>
                {!siteMessagingConfig?.enabled && (
                    <div className={styles.messagingTemporarilyDisabledMsg}>
                        <Translate
                            tid="account_menu_messages_disabled"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </div>
                )}
            </form>
            {showMessageSent && (
                <div className={styles.messageSentOverlay}>
                    <span className={styles.messageSentText}>
                        <Translate
                            tid="account_messages_messageSent"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </span>
                </div>
            )}
        </div>
    );
};

export default ComposeMessage;
